import Dexie from 'dexie';
import { formatISO } from 'date-fns';
import * as Sentry from '@sentry/react';

import type {
  User,
  CreateUserInput,
  UpdateUserInput,
  UserPreferences,
  UpdateUserPreferencesInput,
} from '@/types/user';
import type { Property, CreatePropertyInput, UpdatePropertyInput } from '@/types/property';
import type { Waypoint, CreateWaypointInput, UpdateWaypointInput, WaypointType, BlindMetadata } from '@/types/waypoint';
import type { Season, CreateSeasonInput, UpdateSeasonInput } from '@/types/season';
import { SeasonType } from '@/types/season';
import type { ChecklistTemplate, TemplateTask, CreateTemplateInput, UpdateTemplateInput } from '@/types/checklist-template';
import type { Task, CreateTaskInput, UpdateTaskInput, TaskCategory } from '@/types/task';
import { TimingAnchor } from '@/types/task';
import type { LogEntry, CreateLogEntryInput, UpdateLogEntryInput } from '@/types/log-entry';
import type { PhotoRecord } from '@/types/photo';
import type { ShoppingListItem, CreateShoppingListItemInput, UpdateShoppingListItemInput } from '@/types/shopping-list';
import type { SyncConfigRecord, PendingDeleteRecord } from '@/types/sync';
import type { Hunt, HuntSit, HarvestRecord } from '@/types/hunt';
import type { HuntJournalEntry } from '@/types/hunt-journal-entry';
import { localTodayISO, toLocalISODate } from '@/utils/date-utils';

import type { StorageAdapter } from './storage-adapter';

// Historical sentinel — retained ONLY for v2 schema upgrade (M3 era migration).
// Do not use in runtime entity creation.
const LEGACY_PRE_AUTH_USER_ID = 'local-user';

// ---------------------------------------------------------------------------
// Photo mutation event bus (M83 / ADR-044 §F — W-9 mechanism)
// ---------------------------------------------------------------------------
//
// Module-scoped EventTarget used by usePhotoSrc to subscribe to row-level
// mutations (specifically pendingArrayBufferMigration transitions). Emitted
// from every photo write site that may clear the pending flag:
//   - runPendingPhotoBytesMigration (Phase 2 of v19 migration)
//   - updatePhotoBytes
//   - downloadPhoto (in photo-sync-service.ts, via re-exported helper)
//
// Rationale for EventTarget over Dexie liveQuery (developer choice per ADR-044 §F):
//   1. Avoids introducing a new Dexie subscription primitive that no other
//      surface in the codebase uses today.
//   2. Single-photoId targeting — liveQuery on db.photos would re-run for ANY
//      photo write across the app, not just the one a given usePhotoSrc cares about.
//   3. The set of write sites is small and enumerated below; "missing emit" risk
//      is bounded.

const photoMutations: EventTarget = new EventTarget();

export function getPhotoMutationBus(): EventTarget {
  return photoMutations;
}

export function emitPhotoMutation(photoId: string): void {
  photoMutations.dispatchEvent(
    new CustomEvent('photo-mutated', { detail: { photoId } }),
  );
}

// ---------------------------------------------------------------------------
// Photo bytes migration — module-scoped concurrency + listener state
// (ADR-044 §G/§H — W-10 mutex + W-11 listener cleanup)
// ---------------------------------------------------------------------------

let bytesMigrationInFlight = false;
let onlineHandlerRegistered = false;
let onlineHandler: (() => void) | null = null;

/**
 * Test-only reset for the module-scoped migration state. Production code must
 * never call this. Vitest's module-cache isolation does not fully clear these
 * because the module survives across tests within a single file when the
 * adapter is re-instantiated.
 */
export function _resetPhotoBytesMigrationStateForTesting(): void {
  bytesMigrationInFlight = false;
  if (onlineHandler && typeof window !== 'undefined') {
    window.removeEventListener('online', onlineHandler);
  }
  onlineHandler = null;
  onlineHandlerRegistered = false;
}

// Dexie stores ChecklistTemplate without the `tasks` array (tasks live in `templateTasks` table)
type ChecklistTemplateRecord = Omit<ChecklistTemplate, 'tasks'>;

export class DexieAdapter extends Dexie implements StorageAdapter {
  private currentUserId: string = LEGACY_PRE_AUTH_USER_ID;
  users!: Dexie.Table<User, string>;
  userPreferences!: Dexie.Table<UserPreferences, string>;
  properties!: Dexie.Table<Property, string>;
  waypoints!: Dexie.Table<Waypoint, string>;
  seasons!: Dexie.Table<Season, string>;
  checklistTemplates!: Dexie.Table<ChecklistTemplateRecord, string>;
  templateTasks!: Dexie.Table<TemplateTask, string>;
  tasks!: Dexie.Table<Task, string>;
  logEntries!: Dexie.Table<LogEntry, string>;
  // Photos are NOT in StorageAdapter — local-only blob store, direct Dexie access
  photos!: Dexie.Table<PhotoRecord, string>;
  // Shopping list items (M33)
  shoppingListItems!: Dexie.Table<ShoppingListItem, string>;
  // Sync infrastructure — not part of StorageAdapter
  syncConfig!: Dexie.Table<SyncConfigRecord, string>;
  pendingDeletes!: Dexie.Table<PendingDeleteRecord, string>;
  // Hunt journal (M48)
  hunts!: Dexie.Table<Hunt, string>;
  huntSits!: Dexie.Table<HuntSit, string>;
  harvestRecords!: Dexie.Table<HarvestRecord, string>;
  // Hunt journal entries (M80)
  huntJournalEntries!: Dexie.Table<HuntJournalEntry, string>;

  constructor(name = 'ridgen') {
    super(name);

    // v1 schema kept for migration reference
    this.version(1).stores({
      properties: 'id, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, openerDate, createdAt',
      tasks: 'id, seasonId, propertyId, locationId, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, date, createdAt, *tags',
    });

    // v2 schema adds new tables and indexes
    this.version(2)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        locations: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
        logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      })
      .upgrade(async (tx) => {
        // Migrate existing properties: add userId + optional fields
        await tx
          .table('properties')
          .toCollection()
          .modify((property: Record<string, unknown>) => {
            property['userId'] = LEGACY_PRE_AUTH_USER_ID;
            if (property['state'] === undefined) property['state'] = '';
            if (property['zone'] === undefined) property['zone'] = '';
          });

        // Migrate existing seasons: add userId + seasonType
        await tx
          .table('seasons')
          .toCollection()
          .modify((season: Record<string, unknown>) => {
            season['userId'] = LEGACY_PRE_AUTH_USER_ID;
            if (season['seasonType'] === undefined) season['seasonType'] = SeasonType.Gun;
          });

        // Migrate existing tasks: add v2.0 fields
        await tx
          .table('tasks')
          .toCollection()
          .modify((task: Record<string, unknown>) => {
            if (task['templateTaskId'] === undefined) task['templateTaskId'] = null;
            if (task['category'] === undefined) task['category'] = 'other';
            if (task['selectedAction'] === undefined) task['selectedAction'] = '';
            if (task['timingAnchor'] === undefined) task['timingAnchor'] = TimingAnchor.OpenerRelative;
            if (task['dependsOn'] === undefined) task['dependsOn'] = [];
            if (task['blocked'] === undefined) task['blocked'] = false;
            if (task['notes'] === undefined) task['notes'] = '';
          });

        // Migrate existing logEntries: add propertyId + animal observation fields
        await tx
          .table('logEntries')
          .toCollection()
          .modify((entry: Record<string, unknown>) => {
            if (entry['propertyId'] === undefined) entry['propertyId'] = '';
            if (entry['time'] === undefined) entry['time'] = '';
            if (entry['animalObserved'] === undefined) entry['animalObserved'] = false;
          });
      });

    // v3 schema adds sun time fields to logEntries (M20 — new fields are optional, no migration needed)
    this.version(3).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
    });

    // v4 schema adds weather fields to logEntries (M19+M21 — new fields are optional, no migration needed)
    this.version(4).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
    });

    // v5 schema adds photos table (M23 — blob field NOT indexed per Dexie author guidance)
    this.version(5).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, userId, createdAt',
    });

    // v6 schema adds sync infrastructure (M28 — additive, no migration needed)
    this.version(6).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, userId, createdAt', // remoteUrl/uploadedAt are non-indexed optional fields
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
    });

    // v7 schema adds shoppingListItems table (M33 — additive, no migration needed)
    this.version(7).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
    });

    // v8 schema adds locationId index to photos table (M42 — additive, no migration needed)
    this.version(8).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, locationId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
    });

    // v9 schema adds hunt journal tables (M48 — additive, no migration needed)
    this.version(9).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, locationId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
      hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
      huntSits: 'id, huntId, locationId, userId, date, createdAt',
      harvestRecords: 'id, huntId, huntSitId, locationId, userId, date, createdAt',
    });

    // v10 schema adds huntId index to photos table (M49 — additive, no migration needed)
    this.version(10).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      locations: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
      logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, locationId, huntId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
      hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
      huntSits: 'id, huntId, locationId, userId, date, createdAt',
      harvestRecords: 'id, huntId, huntSitId, locationId, userId, date, createdAt',
    });

    // v11 schema migrates 'stand' locations to 'blind' (Defect Batch B — stand type removed)
    this.version(11)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        locations: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, locationId, category, dueDate, completed, sortOrder',
        logEntries: 'id, locationId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, locationId, huntId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, locationId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, locationId, userId, date, createdAt',
      })
      .upgrade(async (tx) => {
        const standLocations = await tx
          .table('locations')
          .where('type')
          .equals('stand')
          .toArray();

        for (const loc of standLocations) {
          const oldMeta = loc.metadata as Record<string, unknown> | undefined;

          // Map old standType values to new blindType
          let blindType: BlindMetadata['blindType'] = 'other';
          const oldStandType = oldMeta?.standType as string | undefined;
          if (oldStandType === 'ladder') blindType = 'ladder';
          else if (oldStandType === 'hang_on') blindType = 'hang_on';
          else if (oldStandType === 'climbing') blindType = 'other'; // no direct equivalent
          else if (oldStandType === 'saddle') blindType = 'other'; // no direct equivalent
          else if (oldStandType === 'box_blind') blindType = 'elevated_blind';
          else if (oldStandType === 'ground_blind') blindType = 'ground_blind';

          const newMeta: BlindMetadata = {
            type: 'blind',
            blindType,
            heightFeet: typeof oldMeta?.height === 'number' ? oldMeta.height : undefined,
            bestTimeOfDay: oldMeta?.bestTimeOfDay as BlindMetadata['bestTimeOfDay'] | undefined,
            idealWindDirections: Array.isArray(oldMeta?.idealWindDirections)
              ? (oldMeta.idealWindDirections as string[])
              : typeof oldMeta?.bestWindDirection === 'string'
              ? [oldMeta.bestWindDirection]
              : [],
            installedDate: typeof oldMeta?.installedDate === 'string' ? oldMeta.installedDate : undefined,
            lastInspectedDate: typeof oldMeta?.lastInspectedDate === 'string' ? oldMeta.lastInspectedDate : undefined,
          };

          await tx.table('locations').update(loc.id as string, {
            type: 'blind',
            metadata: newMeta,
            updatedAt: new Date().toISOString(),
          });
        }
      });

    // v12 schema renames locations → waypoints (M50 — data deleted first via migration script)
    this.version(12)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
      })
      .upgrade(async (tx) => {
        // In production, data is pre-deleted via delete-location-data.sql /
        // delete-location-data-indexeddb.js before this migration runs, so this
        // copy is effectively a no-op. It is included so the migration chain
        // (v10 → v11 → v12) completes correctly in tests and edge cases.
        const old = await tx.table('locations').toArray();
        if (old.length > 0) {
          await tx.table('waypoints').bulkAdd(old);
        }
      });

    // v13: Add GMU fields to properties. Mark all existing properties as 'pending'
    // so runPendingGmuBackfill() can resolve them asynchronously at boot.
    // Async fetches (point-in-polygon lookup) cannot run inside a versionchange
    // transaction (Safari restriction), so we use a two-phase approach here.
    this.version(13)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
      })
      .upgrade((tx) => {
        // Synchronously mark all properties as pending — async work happens in
        // runPendingGmuBackfill() called at boot outside the versionchange transaction.
        return tx
          .table('properties')
          .toCollection()
          .modify({ gmuStatus: 'pending' });
      });

    // v14: Heal legacy records that slipped through the v13 upgrade with
    // gmuStatus === undefined (e.g. properties synced from Supabase before the
    // mapping fix, or created during a v13 window without the createProperty fix).
    this.version(14)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
      })
      .upgrade(async (tx) => {
        await tx
          .table('properties')
          .toCollection()
          .modify((p: Record<string, unknown>) => {
            if (p['gmuStatus'] === undefined) {
              p['gmuStatus'] = 'pending';
            }
          });
      });

    // v15 schema adds harvestId index to photos table (M58 — additive, no migration needed)
    this.version(15).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      waypoints: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
      logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, waypointId, huntId, harvestId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
      hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
      huntSits: 'id, huntId, waypointId, userId, date, createdAt',
      harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
    });

    // v16 schema adds sitId index to photos table (M69 — sit-level photos, additive, no migration needed)
    this.version(16).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      waypoints: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
      logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, waypointId, huntId, sitId, harvestId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
      hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
      huntSits: 'id, huntId, waypointId, userId, date, createdAt',
      harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
    });

    // v17 schema adds huntJournalEntries table (M80) and removes journalText from hunt records
    this.version(17)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, sitId, harvestId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
        huntJournalEntries: 'id, huntId, userId, createdAt, deletedAt',
      })
      .upgrade(async (tx) => {
        // Remove legacy journalText field from all hunt records — data is intentionally discarded
        // per product decision (M80). The structured entry feed replaces the freeform textarea.
        await tx
          .table('hunts')
          .toCollection()
          .modify((hunt: Record<string, unknown>) => {
            delete hunt['journalText'];
          });
      });

    // v18 (Defect Batch M-07): migrate UserPreferences.defaultPropertyId →
    // Property.isPrimary, then drop the legacy column.
    this.version(18)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, sitId, harvestId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
        huntJournalEntries: 'id, huntId, userId, createdAt, deletedAt',
      })
      .upgrade(async (tx) => {
        // Step 1: collect (userId → defaultPropertyId) from preferences, then
        //         strip defaultPropertyId from every preference record.
        const prefsTable = tx.table('userPreferences');
        const allPrefs: Array<Record<string, unknown>> = await prefsTable.toArray();
        const primaryByUser = new Map<string, string>();
        for (const pref of allPrefs) {
          const userId = pref['userId'];
          const defaultPropertyId = pref['defaultPropertyId'];
          if (typeof userId === 'string' && typeof defaultPropertyId === 'string' && defaultPropertyId.length > 0) {
            primaryByUser.set(userId, defaultPropertyId);
          }
        }
        await prefsTable.toCollection().modify((pref: Record<string, unknown>) => {
          delete pref['defaultPropertyId'];
        });

        // Step 2: backfill Property.isPrimary. For each user, mark the
        //         identified property as primary and explicitly set isPrimary=false
        //         on all others.
        const propsTable = tx.table('properties');
        const now = new Date().toISOString();
        await propsTable
          .toCollection()
          .modify((property: Record<string, unknown>) => {
            const userId = property['userId'];
            const id = property['id'];
            if (typeof userId !== 'string' || typeof id !== 'string') return;
            const target = primaryByUser.get(userId);
            const shouldBePrimary = target === id;
            if (property['isPrimary'] !== shouldBePrimary) {
              property['isPrimary'] = shouldBePrimary;
              property['updatedAt'] = now;
            }
          });
      });

    // v19 (M83 / ADR-044): photo IDB shape change. Phase 1 — synchronously flag
    // every existing row with a legacy `blob` for asynchronous byte conversion by
    // runPendingPhotoBytesMigration() at boot. NO Blob reads happen here — the
    // iOS Safari WebKit 26.4 / iOS 18.7 defect breaks `Blob.arrayBuffer()` on
    // IDB-retrieved Blobs, and we cannot safely call it inside a versionchange
    // transaction. Schema string is unchanged from v18 — pendingArrayBufferMigration
    // is non-indexed per ADR-044 §B (sparse + transient flag).
    this.version(19)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, sitId, harvestId, userId, createdAt',
        shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
        huntJournalEntries: 'id, huntId, userId, createdAt, deletedAt',
      })
      .upgrade(async (tx) => {
        // Phase 1: synchronous flag-flip. Mirrors v13 GMU precedent
        // (dexie-adapter.ts:375-382). NO `await blob.arrayBuffer()` here.
        await tx
          .table('photos')
          .toCollection()
          .modify((photo: Record<string, unknown>) => {
            if (photo['blob'] !== undefined) {
              photo['pendingArrayBufferMigration'] = true;
            }
          });
      });

    // v20 (M97 / #706): Task.templateId provenance for second-checklist append.
    // `templateId` is non-indexed (read via .filter()/in-memory), so the schema
    // string is a VERBATIM mirror of v19 and NO `.upgrade()` callback is needed —
    // existing rows read back with `templateId` undefined (normalized to null on
    // next write). The version bump exists so Dexie opens cleanly post-deploy.
    this.version(20).stores({
      users: 'id, email',
      userPreferences: 'id, userId',
      properties: 'id, userId, name, createdAt',
      waypoints: 'id, propertyId, type, createdAt',
      seasons: 'id, userId, openerDate, seasonType, createdAt',
      checklistTemplates: 'id, seasonType, region, isBuiltIn',
      templateTasks: 'id, templateId, category, sortOrder',
      tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
      logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
      photos: 'id, logEntryId, waypointId, huntId, sitId, harvestId, userId, createdAt',
      shoppingListItems: 'id, propertyId, taskId, purchased, createdAt',
      syncConfig: 'key',
      pendingDeletes: 'id, entityType, deletedAt',
      hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
      huntSits: 'id, huntId, waypointId, userId, date, createdAt',
      harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
      huntJournalEntries: 'id, huntId, userId, createdAt, deletedAt',
    });

    // v21 (M223 / #1308): shoppingListItems.userId index + sentinel-safe backfill.
    // Closes a cross-user data leak — pre-v21 shopping list rows had no userId
    // field at all, so getShoppingList() returned every user's items on a
    // shared device. Backfill assigns the currently-signed-in user's id to
    // existing rows UNLESS the adapter is still on the legacy pre-auth
    // sentinel, in which case userId is left unset (never stamped with the
    // 'local-user' sentinel — CLAUDE.md M82 rule) and a Sentry breadcrumb is
    // emitted so un-owned rows are visible in monitoring (mirrors
    // rejectSentinelRow's breadcrumb pattern in sync-service.ts).
    this.version(21)
      .stores({
        users: 'id, email',
        userPreferences: 'id, userId',
        properties: 'id, userId, name, createdAt',
        waypoints: 'id, propertyId, type, createdAt',
        seasons: 'id, userId, openerDate, seasonType, createdAt',
        checklistTemplates: 'id, seasonType, region, isBuiltIn',
        templateTasks: 'id, templateId, category, sortOrder',
        tasks: 'id, seasonId, propertyId, waypointId, category, dueDate, completed, sortOrder',
        logEntries: 'id, waypointId, propertyId, date, createdAt, *tags',
        photos: 'id, logEntryId, waypointId, huntId, sitId, harvestId, userId, createdAt',
        shoppingListItems: 'id, userId, propertyId, taskId, purchased, createdAt',
        syncConfig: 'key',
        pendingDeletes: 'id, entityType, deletedAt',
        hunts: 'id, userId, seasonId, propertyId, startDate, createdAt',
        huntSits: 'id, huntId, waypointId, userId, date, createdAt',
        harvestRecords: 'id, huntId, huntSitId, waypointId, userId, date, createdAt',
        huntJournalEntries: 'id, huntId, userId, createdAt, deletedAt',
      })
      .upgrade(async (tx) => {
        await tx
          .table('shoppingListItems')
          .toCollection()
          .modify((item: Record<string, unknown>) => {
            if (this.currentUserId !== LEGACY_PRE_AUTH_USER_ID) {
              item['userId'] = this.currentUserId;
            } else {
              Sentry.captureMessage(
                '[dexie-adapter] Shopping list item left un-backfilled during v21 userId migration',
                { level: 'warning', extra: { recordId: item['id'] } },
              );
            }
          });
      });
  }

  /**
   * Phase 2 of the v18 → v19 photo bytes migration (ADR-044 §B).
   *
   * Runs outside any versionchange transaction. Reads the legacy `blob` field
   * from each row flagged `pendingArrayBufferMigration: true`, attempts to
   * extract bytes via `await blob.arrayBuffer()`, and either:
   *   - Case 1: success → writes blobBytes/blobType/blobSize, clears legacy + pending
   *   - Case 2: throws + has remoteUrl + online → clears legacy + pending (re-download on demand)
   *   - Case 3: throws + has remoteUrl + offline → preserves pending (defer until 'online')
   *   - Case 4: throws + no remoteUrl → clears legacy + pending (routes to no-remote-url error)
   *
   * Mutex-guarded against concurrent invocation (ADR-044 §G). Manages an
   * `'online'` event listener for deferred retries (ADR-044 §H) — registers
   * when Case 3 rows remain, removes when all rows drain.
   */
  async runPendingPhotoBytesMigration(): Promise<void> {
    if (bytesMigrationInFlight) return;
    bytesMigrationInFlight = true;
    try {
      // Per ADR-044 §B no-index decision: use .filter(...), not .where(...)
      const pending = await this.photos
        .filter((p) => p.pendingArrayBufferMigration === true)
        .toArray();

      for (const row of pending) {
        await this.processPendingPhotoRow(row);
      }

      // Decide listener registration vs teardown based on drain state
      const stillPending = await this.photos
        .filter((p) => p.pendingArrayBufferMigration === true)
        .toArray();

      if (typeof window !== 'undefined') {
        if (stillPending.length === 0 && onlineHandler) {
          // W-11: teardown after drain
          window.removeEventListener('online', onlineHandler);
          onlineHandler = null;
          onlineHandlerRegistered = false;
        } else if (stillPending.length > 0 && !onlineHandlerRegistered) {
          // W-11: register exactly once for Case 3 deferred retry
          onlineHandler = () => {
            void this.runPendingPhotoBytesMigration().catch((err: unknown) => {
              console.error('[dexie-adapter] online-triggered photo migration failed', err);
            });
          };
          window.addEventListener('online', onlineHandler);
          onlineHandlerRegistered = true;
        }
      }
    } finally {
      bytesMigrationInFlight = false;
    }
  }

  /**
   * Per-row disposition for the Phase 2 photo bytes migration.
   * Pulled out as a private method for readability; see ADR-044 §D for the
   * full disposition table.
   */
  private async processPendingPhotoRow(row: PhotoRecord): Promise<void> {
    const legacyBlob = (row as PhotoRecord & { blob?: Blob }).blob;

    if (legacyBlob === undefined) {
      // Shouldn't happen — Phase 1 only sets the flag when blob is present.
      // Defensive: clear the flag so the row doesn't loop on re-invocation.
      await this.photos.update(row.id, {
        pendingArrayBufferMigration: undefined,
      });
      emitPhotoMutation(row.id);
      return;
    }

    try {
      const bytes = await legacyBlob.arrayBuffer();
      // Case 1: arrayBuffer succeeds
      await this.photos.update(row.id, {
        blobBytes: bytes,
        blobType: legacyBlob.type || 'image/jpeg',
        blobSize: legacyBlob.size,
        blob: undefined,
        pendingArrayBufferMigration: undefined,
      });
      emitPhotoMutation(row.id);
    } catch {
      const hasRemote = typeof row.remoteUrl === 'string' && row.remoteUrl.length > 0;
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;

      if (hasRemote && !online) {
        // Case 3: preserve pending; deferred retry on 'online'
        return;
      }

      // Case 2 (hasRemote && online) OR Case 4 (!hasRemote): drop legacy + pending
      await this.photos.update(row.id, {
        blob: undefined,
        pendingArrayBufferMigration: undefined,
      });
      emitPhotoMutation(row.id);
    }
  }

  /**
   * Resolves GMU for all properties marked `gmuStatus: 'pending'`.
   * Called at boot (main.tsx) after the versionchange transaction completes.
   * Safe to call multiple times — only processes pending records.
   */
  async runPendingGmuBackfill(): Promise<void> {
    const { resolveGmu } = await import('@/services/gmu-service');
    const pending = await this.properties
      .filter((p) => p.gmuStatus === 'pending')
      .toArray();

    for (const property of pending) {
      const result = await resolveGmu({
        state: property.state,
        latitude: property.latitude,
        longitude: property.longitude,
      });

      if (result.status === 'resolved') {
        await this.properties.update(property.id, {
          gmu: result.gmu,
          gmuStatus: 'resolved',
          updatedAt: new Date().toISOString(),
        });
      } else {
        await this.properties.update(property.id, {
          gmu: null,
          gmuStatus: result.status,
          updatedAt: new Date().toISOString(),
        });
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  private assertIdValid(id: unknown, context: string): void {
    if (!id || typeof id !== 'string') {
      throw new Error(
        `[dexie-adapter] Cannot write record with invalid id in ${context}: id=${String(id)} (type: ${typeof id})`,
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Auth & User
  // ---------------------------------------------------------------------------

  setCurrentUserId(userId: string): void {
    this.currentUserId = userId;
  }

  async getCurrentUser(): Promise<User | null> {
    return (await this.users.toArray())[0] ?? null;
  }

  async getUserById(id: string): Promise<User | null> {
    return (await this.users.get(id)) ?? null;
  }

  async createUser(data: CreateUserInput): Promise<User> {
    this.assertIdValid(data.id, 'createUser');
    const now = new Date().toISOString();
    // Billing fields are server-authoritative — default to starter/null on local creation. ADR-032.
    const record: User = {
      ...data,
      tier: 'starter',
      stripeCustomerId: null,
      subscriptionStatus: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.users.put(record);
    return record;
  }

  async updateUser(id: string, data: UpdateUserInput): Promise<User> {
    const existing = await this.users.get(id);
    if (!existing) throw new Error(`User not found: ${id}`);
    const updated: User = { ...existing, ...data, updatedAt: new Date().toISOString() };
    await this.users.put(updated);
    return updated;
  }

  async getUserPreferences(userId: string): Promise<UserPreferences | null> {
    return (await this.userPreferences.where('userId').equals(userId).first()) ?? null;
  }

  async updateUserPreferences(userId: string, data: UpdateUserPreferencesInput): Promise<UserPreferences> {
    const existing = await this.getUserPreferences(userId);
    if (existing) {
      const updated: UserPreferences = { ...existing, ...data, updatedAt: new Date().toISOString() };
      await this.userPreferences.put(updated);
      return updated;
    }
    // Create on first access
    const created: UserPreferences = {
      id: crypto.randomUUID(),
      userId,
      theme: 'system',
      notificationsEnabled: false,
      notificationLeadDays: 3,
      ...data,
      updatedAt: new Date().toISOString(),
    };
    await this.userPreferences.put(created);
    return created;
  }

  // ---------------------------------------------------------------------------
  // Properties
  // ---------------------------------------------------------------------------

  async getProperties(): Promise<Property[]> {
    const all = await this.properties.where('userId').equals(this.currentUserId).toArray();
    return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async getProperty(id: string): Promise<Property | null> {
    return (await this.properties.get(id)) ?? null;
  }

  async createProperty(data: CreatePropertyInput): Promise<Property> {
    const now = new Date().toISOString();

    // Import COVERED_STATES lazily to avoid circular deps — gmu-service is not a
    // StorageAdapter concern, but we need the same set to decide initial gmuStatus.
    // We use a static inline set that mirrors COVERED_STATES so there is no
    // runtime import here (and no async) — sync operation.
    const COVERED = new Set(['WI', 'PA', 'MN', 'MI', 'NY', 'IL', 'IA', 'OH', 'TX', 'GA', 'NC', 'KY', 'IN', 'VA', 'AL', 'SC', 'MO']);
    const gmuStatus: Property['gmuStatus'] = COVERED.has(data.state ?? '') ? 'pending' : undefined;

    const record: Property = {
      ...data,
      id: crypto.randomUUID(),
      userId: this.currentUserId,
      gmuStatus,
      createdAt: now,
      updatedAt: now,
    };
    await this.properties.put(record);
    return record;
  }

  async updateProperty(id: string, data: UpdatePropertyInput): Promise<Property> {
    const existing = await this.properties.get(id);
    if (!existing) throw new Error(`Property not found: ${id}`);

    const merged: Property = { ...existing, ...data, updatedAt: new Date().toISOString() };

    // If lat/lng or state changed, reset gmuStatus to 'pending' so the backfill
    // pipeline re-resolves the GMU. Only applies to COVERED_STATES — same logic
    // as createProperty above.
    const COVERED = new Set(['WI', 'PA', 'MN', 'MI', 'NY', 'IL', 'IA', 'OH', 'TX', 'GA', 'NC', 'KY', 'IN', 'VA', 'AL', 'SC', 'MO']);
    const locationChanged =
      data.latitude !== undefined && data.latitude !== existing.latitude ||
      data.longitude !== undefined && data.longitude !== existing.longitude ||
      data.state !== undefined && data.state !== existing.state;

    if (locationChanged) {
      merged.gmuStatus = COVERED.has(merged.state) ? 'pending' : undefined;
      merged.gmu = null;
    }

    await this.properties.put(merged);
    return merged;
  }

  async deleteProperty(id: string): Promise<void> {
    // Cascade: remove all data tied to this property
    await this.waypoints.where('propertyId').equals(id).delete();
    await this.tasks.where('propertyId').equals(id).delete();
    await this.logEntries.where('propertyId').equals(id).delete();
    await this.properties.delete(id);
  }

  async setPrimaryProperty(propertyId: string): Promise<void> {
    // Mirror setPrimarySeasons: atomic toggle, bump updatedAt only on rows
    // whose isPrimary flag actually changed.
    await this.transaction('rw', this.properties, async () => {
      const all = await this.properties
        .where('userId')
        .equals(this.currentUserId)
        .toArray();
      const now = new Date().toISOString();
      for (const property of all) {
        const shouldBePrimary = property.id === propertyId;
        if (property.isPrimary !== shouldBePrimary) {
          await this.properties.put({
            ...property,
            isPrimary: shouldBePrimary,
            updatedAt: now,
          });
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Waypoints
  // ---------------------------------------------------------------------------

  async getWaypoints(propertyId: string): Promise<Waypoint[]> {
    const results = await this.waypoints.where('propertyId').equals(propertyId).toArray();
    return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async getWaypoint(id: string): Promise<Waypoint | null> {
    return (await this.waypoints.get(id)) ?? null;
  }

  async getWaypointsByType(propertyId: string, type: WaypointType): Promise<Waypoint[]> {
    const results = await this.waypoints
      .where('propertyId')
      .equals(propertyId)
      .filter((wp) => wp.type === type)
      .toArray();
    return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async createWaypoint(data: CreateWaypointInput): Promise<Waypoint> {
    const now = new Date().toISOString();
    const record: Waypoint = { ...data, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    await this.waypoints.put(record);
    return record;
  }

  async updateWaypoint(id: string, data: UpdateWaypointInput): Promise<Waypoint> {
    const existing = await this.waypoints.get(id);
    if (!existing) throw new Error(`Waypoint not found: ${id}`);
    const updated: Waypoint = { ...existing, ...data, updatedAt: new Date().toISOString() };
    await this.waypoints.put(updated);
    return updated;
  }

  async deleteWaypoint(id: string): Promise<void> {
    await this.waypoints.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Seasons
  // ---------------------------------------------------------------------------

  async getSeasons(): Promise<Season[]> {
    const all = await this.seasons.where('userId').equals(this.currentUserId).toArray();
    return all.sort((a, b) => a.openerDate.localeCompare(b.openerDate));
  }

  async getActiveSeasons(): Promise<Season[]> {
    const today = localTodayISO();
    const all = await this.seasons.where('userId').equals(this.currentUserId).toArray();
    return all
      .filter((s) => !s.closerDate || s.closerDate >= today)
      .sort((a, b) => a.openerDate.localeCompare(b.openerDate));
  }

  async getSeason(id: string): Promise<Season | null> {
    return (await this.seasons.get(id)) ?? null;
  }

  async createSeason(data: CreateSeasonInput): Promise<Season> {
    const now = new Date().toISOString();
    const record: Season = {
      ...data,
      id: crypto.randomUUID(),
      userId: this.currentUserId,
      createdAt: now,
      updatedAt: now,
    };
    await this.seasons.put(record);
    return record;
  }

  async updateSeason(id: string, data: UpdateSeasonInput): Promise<Season> {
    const existing = await this.seasons.get(id);
    if (!existing) throw new Error(`Season not found: ${id}`);
    const updated: Season = { ...existing, ...data, updatedAt: new Date().toISOString() };
    await this.seasons.put(updated);
    return updated;
  }

  async deleteSeason(id: string): Promise<void> {
    await this.seasons.delete(id);
  }

  async setPrimarySeasons(seasonId: string): Promise<void> {
    await this.transaction('rw', this.seasons, async () => {
      const all = await this.seasons.where('userId').equals(this.currentUserId).toArray();
      const now = new Date().toISOString();
      for (const season of all) {
        const shouldBePrimary = season.id === seasonId;
        if (season.isPrimary !== shouldBePrimary) {
          await this.seasons.put({ ...season, isPrimary: shouldBePrimary, updatedAt: now });
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Checklist Templates
  // ---------------------------------------------------------------------------

  private async buildTemplate(record: ChecklistTemplateRecord): Promise<ChecklistTemplate> {
    const tasks = await this.templateTasks
      .where('templateId')
      .equals(record.id)
      .sortBy('sortOrder');
    return { ...record, tasks };
  }

  async getTemplates(): Promise<ChecklistTemplate[]> {
    const records = await this.checklistTemplates.toArray();
    return Promise.all(records.map((r) => this.buildTemplate(r)));
  }

  async getTemplatesByType(seasonType: SeasonType): Promise<ChecklistTemplate[]> {
    const records = await this.checklistTemplates
      .where('seasonType')
      .equals(seasonType)
      .toArray();
    return Promise.all(records.map((r) => this.buildTemplate(r)));
  }

  async getTemplate(id: string): Promise<ChecklistTemplate | null> {
    const record = await this.checklistTemplates.get(id);
    if (!record) return null;
    return this.buildTemplate(record);
  }

  async createTemplate(data: CreateTemplateInput): Promise<ChecklistTemplate> {
    const now = new Date().toISOString();
    const id = crypto.randomUUID();
    const { tasks, ...rest } = data;

    const templateRecord: ChecklistTemplateRecord = { ...rest, id, createdAt: now, updatedAt: now };
    await this.checklistTemplates.put(templateRecord);

    const taskRecords: TemplateTask[] = tasks.map((t, i) => ({
      ...t,
      id: t.id || crypto.randomUUID(),
      templateId: id,
      sortOrder: t.sortOrder ?? i,
    }));
    await this.templateTasks.bulkPut(taskRecords);

    return { ...templateRecord, tasks: taskRecords };
  }

  async updateTemplate(id: string, data: UpdateTemplateInput): Promise<ChecklistTemplate> {
    const existing = await this.checklistTemplates.get(id);
    if (!existing) throw new Error(`Template not found: ${id}`);

    const now = new Date().toISOString();
    const { tasks, ...rest } = data;

    const updated: ChecklistTemplateRecord = { ...existing, ...rest, updatedAt: now };
    await this.checklistTemplates.put(updated);

    if (tasks !== undefined) {
      await this.templateTasks.where('templateId').equals(id).delete();
      const taskRecords: TemplateTask[] = tasks.map((t, i) => ({
        ...t,
        id: t.id || crypto.randomUUID(),
        templateId: id,
        sortOrder: t.sortOrder ?? i,
      }));
      await this.templateTasks.bulkPut(taskRecords);
    }

    return this.buildTemplate(updated);
  }

  async deleteTemplate(id: string): Promise<void> {
    await this.templateTasks.where('templateId').equals(id).delete();
    await this.checklistTemplates.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Tasks
  // ---------------------------------------------------------------------------

  async getTasks(seasonId: string): Promise<Task[]> {
    const results = await this.tasks.where('seasonId').equals(seasonId).toArray();
    return results.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async getTasksByCategory(seasonId: string, category: TaskCategory): Promise<Task[]> {
    const results = await this.tasks
      .where('seasonId')
      .equals(seasonId)
      .filter((t) => t.category === category)
      .toArray();
    return results.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async getUpcomingTasks(limit = 10): Promise<Task[]> {
    const userSeasons = await this.seasons.where('userId').equals(this.currentUserId).toArray();
    const seasonIds = new Set(userSeasons.map((s) => s.id));
    const all = await this.tasks.toArray();
    return all
      .filter((t) => !t.completed && seasonIds.has(t.seasonId))
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      .slice(0, limit);
  }

  async getBlockedTasks(seasonId: string): Promise<Task[]> {
    const results = await this.tasks
      .where('seasonId')
      .equals(seasonId)
      .filter((t) => t.blocked === true)
      .toArray();
    return results.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async createTask(data: CreateTaskInput): Promise<Task> {
    const now = new Date().toISOString();
    const record: Task = {
      dependsOn: [],
      blocked: false,
      templateTaskId: null,
      templateId: null,
      notes: '',
      ...data,
      id: data.id ?? crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
    };
    await this.tasks.put(record);
    return record;
  }

  async updateTask(id: string, data: UpdateTaskInput): Promise<Task> {
    const existing = await this.tasks.get(id);
    if (!existing) throw new Error(`Task not found: ${id}`);
    const updated: Task = { ...existing, ...data, updatedAt: new Date().toISOString() };
    await this.tasks.put(updated);
    return updated;
  }

  async completeTask(id: string): Promise<Task> {
    const existing = await this.tasks.get(id);
    if (!existing) throw new Error(`Task not found: ${id}`);
    if (existing.blocked) throw new Error(`Cannot complete blocked task: ${id}`);

    const now = new Date().toISOString();
    const completed: Task = { ...existing, completed: true, completedAt: now, updatedAt: now };
    await this.tasks.put(completed);

    // Recompute blocked state on tasks that depend on this one
    const dependents = await this.tasks.filter((t) => (t.dependsOn ?? []).includes(id)).toArray();

    for (const dependent of dependents) {
      if (!dependent.blocked) continue;
      const depIds = dependent.dependsOn ?? [];
      const deps = await Promise.all(depIds.map((depId) => this.tasks.get(depId)));
      const allComplete = deps.every((d) => d?.completed === true);
      if (allComplete) {
        await this.tasks.put({ ...dependent, blocked: false, updatedAt: now });
      }
    }

    return completed;
  }

  async snoozeTask(id: string, until: Date): Promise<Task> {
    const existing = await this.tasks.get(id);
    if (!existing) throw new Error(`Task not found: ${id}`);
    const updated: Task = {
      ...existing,
      snoozedUntil: toLocalISODate(until),
      updatedAt: new Date().toISOString(),
    };
    await this.tasks.put(updated);
    return updated;
  }

  async deleteTask(id: string): Promise<void> {
    await this.tasks.delete(id);
  }

  async bulkCreateTasks(data: CreateTaskInput[]): Promise<Task[]> {
    const results: Task[] = [];
    await this.transaction('rw', this.tasks, async () => {
      for (const item of data) {
        results.push(await this.createTask(item));
      }
    });
    return results;
  }

  // ---------------------------------------------------------------------------
  // Log Entries
  // ---------------------------------------------------------------------------

  async getLogEntries(waypointId: string): Promise<LogEntry[]> {
    const results = await this.logEntries.where('waypointId').equals(waypointId).toArray();
    return results.sort((a, b) => b.date.localeCompare(a.date));
  }

  async getLogEntriesByProperty(propertyId: string): Promise<LogEntry[]> {
    const results = await this.logEntries
      .where('propertyId')
      .equals(propertyId)
      .toArray();
    return results.sort((a, b) => b.date.localeCompare(a.date));
  }

  async getLogEntriesByDateRange(waypointId: string, start: Date, end: Date): Promise<LogEntry[]> {
    const startStr = formatISO(start, { representation: 'date' });
    const endStr = formatISO(end, { representation: 'date' });
    const results = await this.logEntries
      .where('waypointId')
      .equals(waypointId)
      .filter((e) => e.date >= startStr && e.date <= endStr)
      .toArray();
    return results.sort((a, b) => b.date.localeCompare(a.date));
  }

  async getRecentLogEntries(limit = 10): Promise<LogEntry[]> {
    const userProperties = await this.properties.where('userId').equals(this.currentUserId).toArray();
    const propertyIds = new Set(userProperties.map((p) => p.id));
    const all = await this.logEntries.orderBy('date').reverse().toArray();
    // propertyId is optional until M12 rework — include untagged entries as fallback
    return all.filter((e) => !e.propertyId || propertyIds.has(e.propertyId)).slice(0, limit);
  }

  async createLogEntry(data: CreateLogEntryInput): Promise<LogEntry> {
    const now = new Date().toISOString();
    const record: LogEntry = { ...data, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    await this.logEntries.put(record);
    return record;
  }

  async updateLogEntry(id: string, data: UpdateLogEntryInput): Promise<LogEntry> {
    const existing = await this.logEntries.get(id);
    if (!existing) throw new Error(`LogEntry not found: ${id}`);
    const updated: LogEntry = { ...existing, ...data, updatedAt: new Date().toISOString() };
    await this.logEntries.put(updated);
    return updated;
  }

  async deleteLogEntry(id: string): Promise<void> {
    await this.logEntries.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Shopping List (M33)
  // ---------------------------------------------------------------------------

  async getShoppingList(propertyId?: string): Promise<ShoppingListItem[]> {
    const all = await this.shoppingListItems.where('userId').equals(this.currentUserId).toArray();
    const filtered = propertyId ? all.filter((item) => item.propertyId === propertyId) : all;
    return filtered.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async createShoppingListItem(data: CreateShoppingListItemInput): Promise<ShoppingListItem> {
    const now = new Date().toISOString();
    const record: ShoppingListItem = {
      ...data,
      id: crypto.randomUUID(),
      userId: this.currentUserId,
      createdAt: now,
      updatedAt: now,
    };
    await this.shoppingListItems.put(record);
    return record;
  }

  async updateShoppingListItem(id: string, data: UpdateShoppingListItemInput): Promise<ShoppingListItem> {
    const existing = await this.shoppingListItems.get(id);
    if (!existing) throw new Error(`ShoppingListItem not found: ${id}`);
    const updated: ShoppingListItem = { ...existing, ...data, updatedAt: new Date().toISOString() };
    await this.shoppingListItems.put(updated);
    return updated;
  }

  async deleteShoppingListItem(id: string): Promise<void> {
    await this.shoppingListItems.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Hunt journal (M48)
  // ---------------------------------------------------------------------------

  async createHunt(data: Omit<Hunt, 'id' | 'createdAt' | 'updatedAt'>): Promise<Hunt> {
    const now = new Date().toISOString();
    const record: Hunt = { ...data, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    await this.hunts.put(record);
    return record;
  }

  async getHunt(id: string): Promise<Hunt | null> {
    return (await this.hunts.get(id)) ?? null;
  }

  async getHuntsByProperty(propertyId: string): Promise<Hunt[]> {
    const results = await this.hunts.where('propertyId').equals(propertyId).toArray();
    return results.filter((h) => !h.deletedAt).sort((a, b) => b.startDate.localeCompare(a.startDate));
  }

  async getHuntsBySeason(seasonId: string): Promise<Hunt[]> {
    const results = await this.hunts.where('seasonId').equals(seasonId).toArray();
    return results.filter((h) => !h.deletedAt).sort((a, b) => b.startDate.localeCompare(a.startDate));
  }

  async updateHunt(id: string, updates: Partial<Hunt>): Promise<Hunt> {
    const existing = await this.hunts.get(id);
    if (!existing) throw new Error(`Hunt not found: ${id}`);
    const updated: Hunt = { ...existing, ...updates, updatedAt: new Date().toISOString() };
    await this.hunts.put(updated);
    return updated;
  }

  async deleteHunt(id: string): Promise<void> {
    await this.hunts.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Hunt sits (M48)
  // ---------------------------------------------------------------------------

  async createHuntSit(data: Omit<HuntSit, 'id' | 'createdAt' | 'updatedAt'>): Promise<HuntSit> {
    const now = new Date().toISOString();
    const record: HuntSit = { ...data, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    await this.huntSits.put(record);
    return record;
  }

  async getHuntSit(id: string): Promise<HuntSit | null> {
    return (await this.huntSits.get(id)) ?? null;
  }

  async getHuntSitsByHunt(huntId: string): Promise<HuntSit[]> {
    const results = await this.huntSits.where('huntId').equals(huntId).toArray();
    return results.filter((s) => !s.deletedAt).sort((a, b) => a.date.localeCompare(b.date));
  }

  async updateHuntSit(id: string, updates: Partial<HuntSit>): Promise<HuntSit> {
    const existing = await this.huntSits.get(id);
    if (!existing) throw new Error(`HuntSit not found: ${id}`);
    const updated: HuntSit = { ...existing, ...updates, updatedAt: new Date().toISOString() };
    await this.huntSits.put(updated);
    return updated;
  }

  async deleteHuntSit(id: string): Promise<void> {
    await this.huntSits.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Harvest records (M48)
  // ---------------------------------------------------------------------------

  async createHarvestRecord(data: Omit<HarvestRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<HarvestRecord> {
    const now = new Date().toISOString();
    const record: HarvestRecord = { ...data, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    await this.harvestRecords.put(record);
    return record;
  }

  async getHarvestRecord(id: string): Promise<HarvestRecord | null> {
    return (await this.harvestRecords.get(id)) ?? null;
  }

  async getHarvestRecordsByHunt(huntId: string): Promise<HarvestRecord[]> {
    return this.harvestRecords.where('huntId').equals(huntId).toArray();
  }

  async getHarvestRecordsBySeason(seasonId: string): Promise<HarvestRecord[]> {
    const hunts = await this.getHuntsBySeason(seasonId);
    const huntIds = new Set(hunts.map((h) => h.id));
    const all = await this.harvestRecords.toArray();
    return all.filter((r) => huntIds.has(r.huntId));
  }

  async getAllHarvestRecordsByUser(userId: string): Promise<HarvestRecord[]> {
    return this.harvestRecords.where('userId').equals(userId).toArray();
  }

  async updateHarvestRecord(id: string, updates: Partial<HarvestRecord>): Promise<HarvestRecord> {
    const existing = await this.harvestRecords.get(id);
    if (!existing) throw new Error(`HarvestRecord not found: ${id}`);
    const updated: HarvestRecord = { ...existing, ...updates, updatedAt: new Date().toISOString() };
    await this.harvestRecords.put(updated);
    return updated;
  }

  async deleteHarvestRecord(id: string): Promise<void> {
    await this.harvestRecords.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Hunt journal entries (M80)
  // ---------------------------------------------------------------------------

  async createHuntJournalEntry(entry: HuntJournalEntry): Promise<HuntJournalEntry> {
    await this.huntJournalEntries.put(entry);
    return entry;
  }

  async getHuntJournalEntriesByHunt(huntId: string): Promise<HuntJournalEntry[]> {
    // Use index-based query (CLAUDE.md gotcha: where().equals() not .filter())
    const results = await this.huntJournalEntries.where('huntId').equals(huntId).toArray();
    return results
      .filter((e) => e.deletedAt == null)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async updateHuntJournalEntry(id: string, updates: Partial<HuntJournalEntry>): Promise<HuntJournalEntry> {
    const existing = await this.huntJournalEntries.get(id);
    if (!existing) throw new Error(`HuntJournalEntry not found: ${id}`);
    const updated: HuntJournalEntry = { ...existing, ...updates, updatedAt: new Date().toISOString() };
    await this.huntJournalEntries.put(updated);
    return updated;
  }

  async deleteHuntJournalEntry(id: string): Promise<void> {
    const existing = await this.huntJournalEntries.get(id);
    if (!existing) throw new Error(`HuntJournalEntry not found: ${id}`);
    await this.huntJournalEntries.put({ ...existing, deletedAt: new Date().toISOString() });
  }

  // ---------------------------------------------------------------------------
  // Photo bytes hydration (M83 — renamed from updatePhotoBlob per ADR-044 §E)
  // ---------------------------------------------------------------------------

  /**
   * Write downloaded photo bytes back into an existing photo row without touching metadata.
   * Stores ArrayBuffer (not Blob) per ADR-044 — avoids iOS Safari WebKit IDB-Blob defect.
   * Also stamps updatedAt = now so the push loop syncs the hydrated row on the next cycle.
   * No-op if the photo row does not exist (graceful — hydration may race with delete).
   */
  async updatePhotoBytes(
    photoId: string,
    bytes: ArrayBuffer,
    type: string,
    size: number,
  ): Promise<void> {
    const existing = await this.photos.get(photoId);
    if (!existing) {
      console.warn('[dexie-adapter] updatePhotoBytes: photo not found, skipping', photoId);
      return;
    }
    await this.photos.put({
      ...existing,
      blobBytes: bytes,
      blobType: type,
      blobSize: size,
      updatedAt: new Date().toISOString(),
    });
    emitPhotoMutation(photoId);
  }
}
